// Implements: F-UIX-023, F-MCK-018, F-UIX-009
/**
 * The scopes a mockup may be put in, built from the seats the person holds.
 *
 * A mockup lives at `personal`, `team` or `organization`, never at `platform`,
 * so a platform seat is not an option. `buildScopeOptions` lists the seats only;
 * `creatableScopeOptions` puts the viewer's own personal scope in front of them
 * (owner decision 2026-10-26: a personal mockup generates within the personal
 * monthly quota, so it is offered again when creating and when moving one's own
 * mockup). The value of an option is `kind:id` so two seats of different kinds can
 * never collide; `personal:` with no id is the viewer's own personal scope, which
 * the server completes with the person's id.
 */

import type { ScopeEntry } from "../../../types/api";

export interface ScopeOption {
  readonly value: string;
  readonly label: string;
  readonly kind: "personal" | "team" | "organization";
  readonly id: string | null;
}

/**
 * The value of the scope a mockup has (or of an option).
 *
 * `personal` is the VIEWER's own scope only when the owner id says so: an
 * admin looking at somebody else's personal mockup must not read "Personal" as
 * if it were theirs, so another person's personal scope keeps its id in the
 * value. Pass `viewerId` `null` for an option, which has no owner yet.
 */
export function scopeValue(kind: string, id: string | null, viewerId: string | null = null): string {
  if (kind === "personal") return id === null || id === viewerId ? "personal:" : `personal:${id}`;
  return `${kind}:${id ?? ""}`;
}

export function buildScopeOptions(seats: readonly ScopeEntry[]): readonly ScopeOption[] {
  const options: ScopeOption[] = [];
  for (const seat of seats) {
    if (seat.kind === "platform" || seat.id === null) continue;
    options.push({
      value: scopeValue(seat.kind, seat.id),
      label: seat.name ?? seat.id,
      kind: seat.kind,
      id: seat.id,
    });
  }
  return options;
}

/**
 * Where the viewer may create a mockup (or move one of their own): their own personal scope
 * first, then every organization and team seat. A person with no seat still has somewhere to
 * create. The server still decides (`403`/`422`).
 */
export function creatableScopeOptions(seats: readonly ScopeEntry[], personalLabel: string): readonly ScopeOption[] {
  return [{ value: scopeValue("personal", null), label: personalLabel, kind: "personal", id: null }, ...buildScopeOptions(seats)];
}

/**
 * The option chosen before the person picks one: the first seat, so a person with seats keeps
 * creating where they always did, or the personal scope when they hold none.
 */
export function defaultScopeOption(options: readonly ScopeOption[]): ScopeOption | undefined {
  return options.find((option) => option.kind !== "personal") ?? options[0];
}

/**
 * The words that say where a mockup lives (a card's scope badge): the viewer's own personal scope,
 * somebody else's, or the seat's name; the id when the session holds no seat for it.
 */
export function scopeLabelOf(
  mockup: { readonly scope_kind: string; readonly scope_id: string },
  seats: readonly ScopeEntry[],
  viewerId: string,
  words: { readonly personal: string; readonly personalOther: string },
): string {
  if (mockup.scope_kind === "personal") {
    return mockup.scope_id === viewerId ? words.personal : words.personalOther;
  }
  const seat = buildScopeOptions(seats).find((option) => option.value === scopeValue(mockup.scope_kind, mockup.scope_id));
  return seat?.label ?? mockup.scope_id;
}
